'use client'

import { useEffect, useRef } from 'react'
import Link from 'next/link'
import StoreBadges from '@/app/components/StoreBadges'
import ScanDemoVideo from './ScanDemoVideo'
import { track } from '@/utils/analytics'
import { FIRST_SCAN_CALLOUT_METHOD, type FirstScanPrompt } from '@/app/blog/firstScanPrompts'
import { PlatformNames } from '@/app/components/Platforms'

/**
 * A concrete product-information task after an article, with app and web
 * paths. Copy remains visible before hydration. The unchanged recorded app
 * example demonstrates one workflow, not support for every listed item.
 * Impression means at least half of the task heading was in the viewport.
 */
export interface FirstScanCalloutProps {
  /** Per-article prompt, looked up from FIRST_SCAN_PROMPTS by slug. */
  prompt: FirstScanPrompt
  /** Post slug for analytics attribution. */
  postSlug: string
  /** Post topic for analytics attribution. */
  postTopic: string
}

export default function FirstScanCallout({
  prompt,
  postSlug,
  postTopic,
}: FirstScanCalloutProps) {
  const headingRef = useRef<HTMLHeadingElement>(null)
  const viewedSlugs = useRef(new Set<string>())

  useEffect(() => {
    const heading = headingRef.current
    if (!heading || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5) && !viewedSlugs.current.has(postSlug)) {
        viewedSlugs.current.add(postSlug)
        track('first_scan_callout_impression', {
          cta_surface: 'first_scan_callout', post_slug: postSlug, post_topic: postTopic,
          page_type: 'blog', method_version: FIRST_SCAN_CALLOUT_METHOD,
        })
        observer.disconnect()
      }
    }, { threshold: [0.5] })
    observer.observe(heading)
    return () => observer.disconnect()
  }, [postSlug, postTopic])

  return (
    <section
      aria-labelledby="first-scan-heading"
      className="not-prose mt-12 relative overflow-hidden rounded-3xl border border-warmOrange/20 bg-gradient-to-br from-warmOrange/10 via-cream to-white p-6 sm:p-8 md:p-10"
    >
      {/* Decorative offset accent — taste-skill: break the rectangle so
          the section reads as intentional, not generic CTA. */}
      <div
        aria-hidden="true"
        className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-warmOrange/15 blur-2xl"
      />

      <div className="relative grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center">
        {/* Copy + CTA */}
        <div className="md:col-span-7 min-w-0">
          <p className="text-2xs sm:text-xs font-semibold uppercase tracking-[0.22em] text-warmOrange mb-3">
            Your next product question
          </p>
          <h3
            id="first-scan-heading"
            ref={headingRef}
            className="text-[clamp(1.375rem,3.5vw,1.875rem)] font-bold text-charcoal leading-[1.15] mb-3"
          >
            Start with <span className="text-warmOrange">{prompt.product}</span>.
          </h3>
          <p className="text-base text-gray-700 leading-relaxed mb-4">
            {prompt.payoff}
          </p>

          <div className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1 mb-6 text-sm text-gray-600">
            <span className="font-semibold text-charcoal">Research examples:</span>
            <span className="text-gray-700">{prompt.brands}</span>
          </div>

          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-center">
            <StoreBadges
              source="first_scan_callout"
              ctaPosition="bottom"
              trackingParams={{
                post_slug: postSlug,
                post_topic: postTopic,
                page_type: 'blog',
                scan_target: prompt.product,
                method_version: FIRST_SCAN_CALLOUT_METHOD,
              }}
            />
            <span className="text-xs text-gray-500">
              <PlatformNames /> · current terms in each store
            </span>
          </div>
          <p className="mt-4 text-xs text-gray-600 leading-relaxed">
            Use the exact product name, model and label to check the information behind an assessment. A photo does not measure particles or personal health risk. The examples above are research subjects, not a verified app-coverage list.
          </p>
          <Link href={prompt.webHref}
            onClick={() => track('topic_hub_click', { cta_surface: 'first_scan_callout_web', destination: prompt.webHref, post_slug: postSlug, post_topic: postTopic, page_type: 'blog', method_version: FIRST_SCAN_CALLOUT_METHOD })}
            className="inline-flex items-center mt-3 py-3 min-h-[48px] text-sm font-semibold text-forest underline underline-offset-4">
            {prompt.webLabel} →
          </Link>
        </div>

        {/* Inline scan video */}
        <div className="md:col-span-5 min-w-0">
          <ScanDemoVideo variant="inline" surface="first_scan_callout_video" />
        </div>
      </div>
    </section>
  )
}
